import React from 'react';
import { Box, Skeleton } from '@mui/material';
import { useParams, useNavigate, useLocation, useOutletContext } from 'react-router-dom';
import { useSelector } from 'react-redux';
import AIResponseLoader from '../../common/AIResponseLoader';
import AIResponseError from '../../common/AIResponseError';
import BreadcrumbCrumb from '../../common/Breadcrumbs/BreadcrumbCrumb';
import PageBreadcrumbs from '../../common/Breadcrumbs/PageBreadcrumbs';
import { TruncatedTitle } from '../../common/TruncatedTitle';
import DeckHeader from './DeckHeader';
import DeckBody from './DeckBody';
import { getData } from '../../../DataAccessLayer';
import { flashcards } from '../../../DataAccessLayer/services';
import { RESOURCE_STATUS, RESOURCE_ERROR_MESSAGES } from '../../common/constants';

function DeckDetails() {
  const { deckId } = useParams();
  const navigate = useNavigate();
  const { state } = useLocation();
  const knownDeckTitle = state?.title;
  const isImpersonating = useSelector((state) => state.impersonation.isImpersonating);
  const courseId = useSelector((state) => state.course.courseId);
  const showBanner = isImpersonating || !!courseId;

  const { deck, setDeck, isLoading, loadError } = useOutletContext();

  return (
    <Box className={'mss__deckDetails' + (showBanner ? ' impersonating' : '')} id="deckDetails">
      <PageBreadcrumbs id="deckDetails__nav_breadcrumbs" rootTo="../.." isVisible={!loadError}>
        <BreadcrumbCrumb isLoading={isLoading && !knownDeckTitle}>
          <TruncatedTitle name={deck?.title ?? knownDeckTitle} variant="inherit" />
        </BreadcrumbCrumb>
      </PageBreadcrumbs>

      {isLoading && (
        <Skeleton
          id="deckDetails__skeleton_loading"
          variant="rectangular"
          height={240}
          sx={{ mt: '1rem' }}
        />
      )}

      {loadError && (
        <AIResponseError
          message={loadError}
          onGoToDashboard={() => navigate('../..', { relative: 'path' })}
        />
      )}

      {!isLoading && !loadError && deck?.status === RESOURCE_STATUS.IN_PROGRESS && (
        <AIResponseLoader
          poll={() => getData(flashcards({ id: deckId, courseId }))}
          isComplete={(data) => data.status === RESOURCE_STATUS.COMPLETED}
          isErrored={(data) => data.status === RESOURCE_STATUS.ERROR}
          onComplete={(data) => setDeck(data)}
          resource="Flashcards"
          onGenerateNew={() => navigate('..', { relative: 'path' })}
          onGoToDashboard={() => navigate('../..', { relative: 'path' })}
        />
      )}

      {!isLoading && !loadError && deck?.status === RESOURCE_STATUS.ERROR && (
        <AIResponseError
          message={RESOURCE_ERROR_MESSAGES[deck.errorCode]?.('flashcard deck')}
          resource="Flashcards"
          onGenerateNew={() => navigate('..', { relative: 'path' })}
          onGoToDashboard={() => navigate('../..', { relative: 'path' })}
        />
      )}

      {!isLoading && !loadError && deck?.status === RESOURCE_STATUS.COMPLETED && (
        <>
          <DeckHeader deck={deck} />
          <DeckBody cards={deck.cards} />
        </>
      )}
    </Box>
  );
}

export default DeckDetails;
